import { RopeSim } from "./ropeSim";
import { SIM_DT } from "./config";
import type { VineRef } from "./benjiSim";
import type { RopeClosestPoint, RopePathPoint, RopePoint } from "./ropeGeometry";

const BROKEN_FADE_SECONDS = 0.7;

/**
 * Headless vine: owns a {@link RopeSim} and the gameplay flags (active /
 * broken). Satisfies {@link VineRef} so BenjiSim can hang/slide on it. No
 * PixiJS — the client mesh renders from `points`.
 *
 * Extracted behaviour-preserving from the client `Vine`.
 */
export class VineState implements VineRef {
  readonly rope: RopeSim;
  pivotWorldX: number;
  pivotWorldY: number;
  readonly length: number;
  active = false;
  broken = false;
  private brokenLife = 0;

  constructor(worldX: number, worldY: number, length: number, windSeed: number) {
    this.pivotWorldX = worldX;
    this.pivotWorldY = worldY;
    this.length = length;
    const segments = Math.max(10, Math.round(length / 20));
    this.rope = new RopeSim(worldX, worldY, length, segments, windSeed);
  }

  get points(): RopePoint[] {
    return this.rope.points;
  }

  /** Point positions at the start of the current tick (render interpolation). */
  get prevPoints(): { x: number; y: number }[] {
    return this.rope.prevPoints;
  }

  /** Brake of one fixed `SIM_DT` (was `simulate(dt)`). */
  step() {
    this.rope.step();
  }

  pinGrip(distanceAlong: number, worldX: number, worldY: number) {
    this.rope.setGripPin(distanceAlong, worldX, worldY);
  }
  pinTip(worldX: number, worldY: number) {
    this.pinGrip(this.length, worldX, worldY);
  }
  releaseTip() {
    this.rope.releaseGrip();
  }

  breakAt(distanceAlong: number, vx: number, vy: number) {
    this.active = false;
    this.broken = true;
    this.brokenLife = 0;
    this.releaseTip();
    this.rope.releaseTop();
    this.rope.applyImpulseAt(distanceAlong, vx, vy);
  }

  applyImpulseAt(distanceAlong: number, vx: number, vy: number) {
    this.rope.applyImpulseAt(distanceAlong, vx, vy);
  }

  tipX(): number {
    return this.rope.tipX();
  }

  pointAtDistance(distanceAlong: number): RopePathPoint {
    return this.rope.pointAtDistance(distanceAlong);
  }

  sweptClosestPointTo(
    a0x: number,
    a0y: number,
    a1x: number,
    a1y: number,
  ): RopeClosestPoint {
    return this.rope.sweptClosestPointTo(a0x, a0y, a1x, a1y);
  }

  setPivot(worldX: number, worldY: number) {
    this.pivotWorldX = worldX;
    this.pivotWorldY = worldY;
    this.rope.setPivot(worldX, worldY);
  }

  /** Fade progress in [0,1] for the renderer (1 = fully broken/gone). */
  get brokenProgress(): number {
    return Math.min(1, this.brokenLife / BROKEN_FADE_SECONDS);
  }

  /** Advance the broken fade; returns true once the vine should be removed. */
  updateBroken(): boolean {
    if (!this.broken) return false;
    this.brokenLife += SIM_DT;
    return this.brokenLife > BROKEN_FADE_SECONDS;
  }
}

/**
 * Headless vine collection: spawn / cull, advances every vine one step and
 * removes broken-faded or off-screen-left ones. The client `VineField` mirrors
 * this for rendering.
 */
export class VineFieldSim {
  readonly vines: VineState[] = [];

  spawn(worldX: number, worldY: number, length: number, windSeed: number): VineState {
    const v = new VineState(worldX, worldY, length, windSeed);
    this.vines.push(v);
    return v;
  }

  clear() {
    this.vines.length = 0;
  }

  remove(vine: VineState): boolean {
    const idx = this.vines.indexOf(vine);
    if (idx < 0) return false;
    this.vines.splice(idx, 1);
    return true;
  }

  /** One fixed step for every vine + cull. `cullLeftWorldX` must be derived
   * deterministically (NOT from a camera) so client and server agree. */
  updateAll(cullLeftWorldX: number) {
    for (let i = this.vines.length - 1; i >= 0; i--) {
      const v = this.vines[i];
      v.step();
      if (v.updateBroken() || (!v.active && v.pivotWorldX < cullLeftWorldX)) {
        this.vines.splice(i, 1);
      }
    }
  }
}
